import request from 'api/requestUtils'
import { paths } from '@luxuryescapes/contract-customer-communication'
import { mapSubscriptionToServer, mapSubscriptionV2ToServer, mapUserPreferences, mapUserPreferencesV2 } from './mappers/LESubscriptionsMap'
import { formatBrazeUserId } from './lib/formatBrazeUserId'

type fetchLEUserSubscriptionsResponse = paths['/api/customer-communication/v1/subscriptions/users/{userId}']['get']['responses']['200']['content']['application/json']['result']
type fetchLEUserSubscriptionsV2Response = paths['/api/customer-communication/v2/subscriptions/users/{userId}']['get']['responses']['200']['content']['application/json']['result']
type updateLEUserSubscriptionsResponse = paths['/api/customer-communication/v1/subscriptions/users']['post']['responses']['200']['content']['application/json']['result']
type updateLEUserSubscriptionsRequest = Omit<paths['/api/customer-communication/v1/subscriptions/users']['post']['parameters']['body']['payload'], 'brand'>
type updateLEUserSubscriptionsV2Response = paths['/api/customer-communication/v2/subscriptions/users']['patch']['responses']['200']['content']['application/json']
type updateLEUserSubscriptionsV2Request = Omit<paths['/api/customer-communication/v2/subscriptions/users']['patch']['parameters']['body']['payload'], 'brand'>
type fetchResubscribeModalDismissedDateResponse = paths['/api/customer-communication/v1/modals/resubscribe/users/{userId}']['get']['responses']['200']['content']['application/json']['result']
type updateResubscribeModalDismissedDateResponse = paths['/api/customer-communication/v1/modals/resubscribe/users/{userId}/actions/{action}']['put']['responses']['200']['content']['application/json']
type fetchTravelPreferencesDismissResponse = paths['/api/customer-communication/v2/users/{userId}/interactions/travel']['get']['responses']['200']['content']['application/json']['result']

export enum SubscriptionSource {
  PREFERENCE_CENTRE = 'PREFERENCE_CENTRE',
  RESUBSCRIBE_MODAL = 'RESUBSCRIBE_MODAL',
  CHECKOUT_PAGE = 'CHECKOUT_PAGE',
  ONE_CLICK_UNSUBSCRIBE = 'ONE_CLICK_UNSUBSCRIBE',
  EMAIL_FOOTER = 'EMAIL_FOOTER',
  SUBSCRIPTION_LINK = 'SUBSCRIPTION_LINK',
  LOYALTY_ENROL_MODAL = 'LOYALTY_ENROL_MODAL',
  UNKNOWN = 'UNKNOWN',
}

export interface SubscriptionPayload {
  userId: string
  subscriptions: App.LESubscriptionsSettings | App.WLSubscriptionsSettings
  source: SubscriptionSource
}

export interface SubscriptionPayloadV2 {
  userId: string
  subscriptions: Partial<App.SubscriptionPreferencesV2>
  bzt?: string
  source: SubscriptionSource
  action?: 'subscribeToCommunications500' | 'subscribeToCommunications750'
  subscriptionKey?: string
}

export function fetchLEUserSubscriptions(userId: string) {
  return request.get<App.ApiResponse<fetchLEUserSubscriptionsResponse>>(`/api/customer-communication/v1/subscriptions/users/${formatBrazeUserId(userId)}`, {
    credentials: 'include',
  },
  ).then((res) => mapUserPreferences(res.result))
}

export function fetchLEUserSubscriptionsV2(userId: string, subscriptionKey?: string) {
  const url = subscriptionKey
    ? `/api/customer-communication/v2/subscriptions/users/${userId}?subscriptionKey=${encodeURIComponent(subscriptionKey)}`
    : `/api/customer-communication/v2/subscriptions/users/${userId}`

  return request.get<App.ApiResponse<fetchLEUserSubscriptionsV2Response>>(url, {
    credentials: 'include',
  },
  ).then((res) => mapUserPreferencesV2(res.result.subscriptions))
}

export function updateLEUserSubscriptions(data: SubscriptionPayload) {
  const payload: updateLEUserSubscriptionsRequest = {
    subscriptions: mapSubscriptionToServer(data.subscriptions),
    userId: formatBrazeUserId(data.userId),
    source: data.source,
  }
  return request.post<updateLEUserSubscriptionsResponse, updateLEUserSubscriptionsRequest>('/api/customer-communication/v1/subscriptions/users', payload, {
    credentials: 'include',
  },
  ).then(() => payload.subscriptions as App.LESubscriptionsSettings)
}

export function updateLEUserSubscriptionsV2(data: SubscriptionPayloadV2) {
  const payload: updateLEUserSubscriptionsV2Request = {
    subscriptions: mapSubscriptionV2ToServer(data.subscriptions),
    userId: data.userId,
    source: data.source,
    action: data.action,
  }

  const baseUrl = '/api/customer-communication/v2/subscriptions/users'

  const params = new URLSearchParams()
  data.subscriptionKey && params.set('subscriptionKey', data.subscriptionKey)
  data.bzt && params.set('brazeUrl', data.bzt)

  const url = params.toString()
    ? `${baseUrl}?${params}`
    : baseUrl

  return request.patch<updateLEUserSubscriptionsV2Response, updateLEUserSubscriptionsV2Request>(url, payload, {
    credentials: 'include',
  }).then(res => mapUserPreferencesV2(res.result.subscriptions))
}

export function fetchResubscribeModalDismissedDate(userId: string) {
  return request.get<App.ApiResponse<fetchResubscribeModalDismissedDateResponse>>(`/api/customer-communication/v1/modals/resubscribe/users/${userId}`, {
    credentials: 'include',
  }).then(res => res.result.resubscribeModal.lastDismissedAt || null)
}

export function updateResubscribeModalDismissedDate(userId: string) {
  return request.put(`/api/customer-communication/v1/modals/resubscribe/users/${userId}/actions/dismissed`, {}, {
    credentials: 'include',
  }).then((res: updateResubscribeModalDismissedDateResponse) => res.result.resubscribeModal.lastDismissedAt)
}

export function sendEmailResubscribePromoCode(userId: string) {
  return request.post('/api/customer-communication/v2/events', {
    userId,
    eventType: 'resubscribed-via-modal',
  }, { credentials: 'include' })
}

export function fetchTravelPreferencesModalDismissedDate(userId: string) {
  return request.get<App.ApiResponse<fetchTravelPreferencesDismissResponse>>(`/api/customer-communication/v2/users/${userId}/interactions/travel`, {
    credentials: 'include',
  }).then(res => res.result.isTravelPrefViewed)
}

export function updateTravelPreferencesModalDismissedDate(userId: string, isTravelPrefViewed: boolean) {
  return request.put(`/api/customer-communication/v2/users/${userId}/interactions/travel`, { isTravelPrefViewed }, {
    credentials: 'include',
  })
}
